import { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { NAVIGATION } from '../lib/nav';
import { isLettingsDesk } from '../lib/lettings';
import { calendarHasAnything } from '../lib/calendar';
import { messagesApi } from '../lib/comms';

/** The home screen owns three paths: the resolved one, the forced overview and a saved view. */
const isHomePath = (p: string) => p === '/app' || p === '/app/overview' || p.startsWith('/app/v/');
import { useAuth } from '../stores/auth';
import { AssistantDrawer } from './AssistantDrawer';
import { HeaderBell } from './Bell';
import { AssistantOrb } from './AssistantOrb';
import { TenantShell } from './tenant/TenantShell';
import { useResidentPortal, useApplicantPortal } from '../lib/resident';
import { HeaderSearch } from './SearchPalette';
import { ThemeMenu } from './ThemeMenu';
import { useThemeSync, themeOf } from '../lib/theme';
import { useHeaderSlot } from '../lib/headerSlot';
import { PageActions } from './ui/menu';
import { BrandIntro } from './BrandLoader';
import { prefersReducedMotion } from '../lib/lottie';
import { holdAppReady, introPending, clearIntro } from '../lib/appReady';

/** Tailwind's `lg` (1024): the page's controls mount in the bar above it and in a row below it, never both. */
function useMinWidth(px: number) {
  const [v, set] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${px}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${px}px)`);
    const h = (e: MediaQueryListEvent) => set(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [px]);
  return v;
}

function Icon({ name, className }: { name: string; className?: string }) {
  const C = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name];
  return C ? <C className={className} /> : <Icons.Circle className={className} />;
}

/**
 * The single application shell. There is no per-role layout: the sidebar is derived
 * from the user's permissions, so a technician, an accountant and the CEO all run
 * exactly this component and simply see different things in it.
 */
/** The header's messages icon: the inbox, with the unread count the sidebar used to show. */
function HeaderMessages({ unread }: { unread: number }) {
  return (
    <Link to="/app/messages" title={unread ? `${unread} unread` : 'Messages'} aria-label="Messages"
      className={`relative grid h-8 w-8 place-items-center rounded-lg transition hover:bg-ink-100 hover:text-ink-900 ${unread ? 'text-ink-700' : 'text-ink-500'}`}>
      <Icons.MessagesSquare className="h-4 w-4" />
      {unread > 0 && (
        <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-on-primary ring-2 ring-white">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}

/**
 * The application shell. A resident or an applicant gets the portal frame (TenantShell: the phone app's look, one
 * navigation); everyone else gets the staff shell below.
 */
export function AppLayout() {
  const resident = useResidentPortal();
  const applicant = useApplicantPortal();
  if (resident) return <TenantShell kind="resident" />;
  if (applicant) return <TenantShell kind="applicant" />;
  return <StaffLayout />;
}

function StaffLayout() {
  const { user, logout, canAccessPage, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // What the current page put in the top bar (title, controls, ⋯ menu).
  const slot = useHeaderSlot();
  const lgUp = useMinWidth(1024);
  // The sidebar's open group: the active page's group, until the user opens another.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const toggleGroup = (g: string) => setOpenGroup((cur) => (cur === g ? null : g));
  // Light / dark / match the device, per user (Task R, prototype §39).
  useThemeSync(themeOf(user));
  // The boot loader waits for the shell's first paint (and for whatever the page holds it for).
  useEffect(() => {
    const release = holdAppReady();
    const frame = requestAnimationFrame(() => release());
    return () => { cancelAnimationFrame(frame); release(); };
  }, []);
  // The brand intro, once, straight after signing in — never on a reload, never with reduced motion.
  // The flag is spent as soon as it is read, so reloading mid-intro does not replay it.
  const [intro, setIntro] = useState(() => introPending() && !prefersReducedMotion());
  useEffect(() => { clearIntro(); }, []);

  const scope = user?.scope.type;
  // A read-only calendar (a resident's) earns its nav item only when something is in it.
  const probeCalendar = canAccessPage('calendar') && !can('calendar.create');
  const calendarProbe = useQuery({ queryKey: ['/calendar', 'probe'], queryFn: calendarHasAnything, enabled: probeCalendar, staleTime: 5 * 60_000 });
  const probeOk = (p: 'calendar' | undefined) => !p || (p === 'calendar' && (!probeCalendar || calendarProbe.data === true));
  // Unread messages: beside a resident's "Messages" in the sidebar, and on the header's messages icon for staff.
  const unread = useQuery({ queryKey: ['/messages/unread'], queryFn: messagesApi.unread, enabled: canAccessPage('messages'), refetchInterval: 60_000 });
  const badgeOf = (b: 'messages' | undefined) => (b === 'messages' ? unread.data?.unread ?? 0 : 0);
  const desk = isLettingsDesk(user, can);
  const groups = NAVIGATION
    .map((g) => ({ ...g, items: g.items.filter((i) => (canAccessPage(i.page) || !!i.alsoPages?.some(canAccessPage) || (scope !== 'OWN' && !!i.alsoPagesNotOwn?.some(canAccessPage))) && (!i.onlyScope || scope === i.onlyScope) && (!i.hideForScope || scope !== i.hideForScope) && (!i.hideWith || !can(i.hideWith)) && (!i.onlyWith || can(i.onlyWith)) && probeOk(i.probe)) }))
    // A Lettings-only member of staff sees the Lettings group alone (NavGroup.lettingsDesk).
    .filter((g) => g.items.length > 0 && (!desk || g.lettingsDesk));

  const current = useMemo(() => {
    // The items this user can see first: a path can sit in two groups (Messages is under
    // Home for a resident and under Managers for staff).
    const all = [...groups, ...NAVIGATION].flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));
    const match = all.filter((i) => i.path === '/app' ? isHomePath(location.pathname) : location.pathname.startsWith(i.path) || !!i.matches?.some((m) => location.pathname.startsWith(m))).sort((a, b) => b.path.length - a.path.length)[0];
    if (location.pathname.startsWith('/app/change-password')) return { group: 'Account', label: 'Change password' };
    return match ?? { group: 'Home', label: 'Dashboard' };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, scope, groups.length]);
  useEffect(() => { setOpenGroup(current.group); }, [current.group]);
  // ⌘K / Ctrl+K opens the assistant from anywhere; "/" opens search (unless you are typing somewhere).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); if (can('assistant.use')) setAssistantOpen(true); }
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); setSearchOpen(true); }
      if (e.key === 'Escape') { setAssistantOpen(false); setMenuOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [can]);

  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('') || user?.email?.[0]?.toUpperCase() || '?';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  return (
    <div data-app className="flex h-screen overflow-hidden bg-white">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-[210px] transform flex-col border-r border-ink-100 bg-ink-50 text-ink-500 transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="safe-top flex h-[52px] shrink-0 items-center gap-2 border-b border-ink-100 px-3">
          {/* The logo goes to the public website. */}
          <Link to="/" className="flex min-w-0 flex-1 items-center gap-2" aria-label="Lotus Living website">
            <img src="/brand/mark-light.svg" alt="" className="h-6 w-6 shrink-0" />
            <span className="truncate text-[13px] font-semibold text-ink-900">Lotus Living</span>
          </Link>
          <button className="grid h-7 w-7 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close menu"><Icons.X className="h-4 w-4" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {groups.map((group) => {
            // A group with one item (staff's Home holds only Dashboard) is that item, flat — no dropdown to open. A short
            // sidebar (an agent's Dashboard, My invoices, Lettings) is flat too: nothing to fold when it all fits.
            const flat = groups.reduce((n, g) => n + g.items.length, 0) <= 5;
            const single = flat || groups.length === 1 || (group.items.length === 1 && !group.keepGroup);
            const isOpen = single || openGroup === group.group;
            const holdsActive = group.group === current.group;
            const groupBadge = group.items.reduce((n, i) => n + badgeOf(i.badge), 0);
            return (
              <div key={group.group}>
                {/* The group is a dropdown (samksly's parent tab): one open at a time, the active page's group opens itself. */}
                {!single && (
                  <button type="button" onClick={() => toggleGroup(group.group)} aria-expanded={isOpen}
                    className="flex w-full items-center gap-2 rounded-lg px-2 pb-1 pt-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-400 transition hover:text-ink-700">
                    <span className="flex-1 truncate">{group.group}</span>
                    {!isOpen && holdsActive && <span className="h-1.5 w-1.5 rounded-full bg-bronze-500" aria-hidden />}
                    {!isOpen && groupBadge > 0 && <span className="rounded-full bg-ink-100 px-1.5 text-[10.5px] font-semibold tabular-nums tracking-normal text-ink-600">{groupBadge}</span>}
                    <Icons.ChevronDown className={`h-3 w-3 shrink-0 text-ink-300 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
                <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
                  {/* min-h-0 lets the row collapse to nothing; the padding sits inside so it collapses too. */}
                  <div className="min-h-0 overflow-hidden">
                    <div className={single ? '' : 'pb-1'}>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/app' || item.path === '/app/my-home'}
                        tabIndex={isOpen ? 0 : -1}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                          `group/nav mb-px flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12.5px] transition ${
                            isActive || (item.path === '/app' && isHomePath(location.pathname)) || !!item.matches?.some((m) => location.pathname.startsWith(m))
                              ? 'is-active bg-bronze-50 font-semibold text-bronze-600'
                              : 'font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900'
                          }`
                        }
                      >
                        <Icon name={item.icon} className="h-[15px] w-[15px] shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {badgeOf(item.badge) > 0 && <span className="rounded-full bg-ink-100 px-1.5 text-[10.5px] font-semibold tabular-nums text-ink-600 group-[.is-active]/nav:bg-bronze-100 group-[.is-active]/nav:text-bronze-700">{badgeOf(item.badge)}</span>}
                      </NavLink>
                    ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="safe-bottom [--safe-pad:0.5rem] relative shrink-0 border-t border-ink-100 px-2 py-2">
          {/* The user menu (prototype §5): look, change password, sign out. */}
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div role="menu" className="sk-fade-in absolute bottom-full left-2 right-2 z-50 mb-1.5 overflow-hidden rounded-lg border border-ink-200 bg-white text-ink-800 shadow-float">
                <div className="border-b border-ink-100 px-3 py-2.5">
                  <div className="truncate text-[12.5px] font-semibold text-ink-900">{fullName}</div>
                  <div className="truncate text-[11.5px] text-ink-500">{user?.email}</div>
                </div>
                <div className="border-b border-ink-100 py-1.5">
                  <ThemeMenu className="px-3 py-1" />
                </div>
                <Link to="/app/change-password" role="menuitem" onClick={() => { setMenuOpen(false); setSidebarOpen(false); }} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] hover:bg-ink-50">
                  <Icons.KeyRound className="h-3.5 w-3.5 text-ink-400" />Change password
                </Link>
                <button role="menuitem" onClick={async () => { setMenuOpen(false); await logout(); navigate('/login'); }} className="mb-1 flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-ink-50">
                  <Icons.LogOut className="h-3.5 w-3.5 text-ink-400" />Sign out
                </button>
              </div>
            </>
          )}
          <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-haspopup="menu" aria-expanded={menuOpen} className="flex w-full items-center gap-2 rounded-lg px-1 py-0.5 text-left transition hover:bg-ink-100">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-bronze-50 text-[11px] font-bold text-bronze-600">{initials}</div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[12px] font-semibold text-ink-900">{fullName}</div>
              <div className="truncate text-[11px] text-ink-500">{user?.roleName}</div>
            </div>
            <Icons.ChevronsUpDown className="h-3.5 w-3.5 text-ink-400" />
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-ink-900/30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          The top bar is the page header (samksly's slab): the page's eyebrow, title, controls and ⋯ menu arrive
          through <PageHeader>; the page body below starts straight away with its content.
        */}
        <header className="safe-top relative z-20 flex h-[52px] shrink-0 items-center gap-1.5 border-b border-ink-100 bg-white px-3 sm:gap-2 lg:px-4">
          <button className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900 lg:hidden" onClick={() => setSidebarOpen((v) => !v)} aria-label="Menu">
            <Icons.Menu className="h-[18px] w-[18px]" />
          </button>
          {/* Back: to the page's parent when it names one, otherwise one step back in history. */}
          <button type="button" onClick={() => (slot?.back ? navigate(slot.back) : navigate(-1))} aria-label="Back" title="Back"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900">
            <Icons.ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate font-display text-[17px] font-semibold tracking-[-0.01em] text-ink-900">{slot?.title ?? current.label}</div>
            {slot?.eyebrow && <div className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-400">{slot.eyebrow}</div>}
          </div>

          {/* The page's own controls (not buttons) sit beside its ⋯ menu; on a phone they drop to a row below. */}
          {lgUp && slot?.actions && <div className="flex shrink-0 items-center gap-1.5">{slot.actions}</div>}
          {slot?.menu && <PageActions items={slot.menu} />}
          {(slot?.actions || slot?.menu) && <span className="mx-0.5 hidden h-5 w-px bg-ink-200 lg:block" aria-hidden />}

          {/* Global search (Task R): "/" anywhere, or click; it opens in place. ⌘K stays with the assistant. */}
          <HeaderSearch open={searchOpen} onOpen={() => setSearchOpen(true)} onClose={() => setSearchOpen(false)} />

          {/* Messages beside the bell for staff (a resident has it under Home and in the portal's tab bar). */}
          {canAccessPage('messages') && scope !== 'OWN' && <HeaderMessages unread={unread.data?.unread ?? 0} />}
          <HeaderBell />
        </header>
        {!lgUp && slot?.actions && (
          <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-ink-100 bg-white px-3 py-1.5 [scrollbar-width:none]">{slot.actions}</div>
        )}

        <main className="flex-1 overflow-y-auto bg-white p-3 lg:pb-3 lg:pl-[15px] lg:pr-2.5 lg:pt-2.5">
          <Outlet />
        </main>
      </div>

      {/* Ask Lotus floats as an orb (bottom-right to start, drag it anywhere); ⌘K opens it too. */}
      {can('assistant.use') && <AssistantOrb onOpen={() => setAssistantOpen(true)} />}
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      {intro && <BrandIntro onDone={() => setIntro(false)} />}
    </div>
  );
}
